import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { reservationsApi, importApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import { Plus, Trash2, Upload, FileSpreadsheet, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const PLATFORMS = [
  { value: 'airbnb', label: 'Airbnb', color: 'bg-rose-500/20 text-rose-400' },
  { value: 'booking', label: 'Booking.com', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'direct', label: 'Direct', color: 'bg-primary/20 text-primary' },
];

export default function ReservationsPage() {
  const { selectedProperty, properties } = useOutletContext();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importPropertyId, setImportPropertyId] = useState('');
  const [formData, setFormData] = useState({
    property_id: '',
    platform: 'airbnb',
    guest_name: '',
    guest_email: '',
    check_in: '',
    check_out: '',
    total_price: '',
    platform_commission: '',
    cleaning_fee: '',
    local_taxes: '',
    notes: '',
  });

  useEffect(() => {
    loadReservations();
  }, [selectedProperty]);

  useEffect(() => {
    if (properties.length > 0 && !importPropertyId) {
      setImportPropertyId(properties[0].id);
    }
  }, [properties]);

  const loadReservations = async () => {
    setLoading(true);
    try {
      const params = selectedProperty !== 'all' ? { property_id: selectedProperty } : {};
      const response = await reservationsApi.getAll(params);
      setReservations(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const onDropCSV = useCallback(async (acceptedFiles, rejectedFiles) => {
    console.log('onDropCSV called:', { acceptedFiles, rejectedFiles });
    
    // Handle rejected files
    if (rejectedFiles && rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors) {
        const errorMessages = rejection.errors.map(e => e.message).join(', ');
        toast.error(`Fichier refusé: ${errorMessages}`);
      }
      return;
    }

    const file = acceptedFiles[0];
    if (!file) {
      toast.error('Aucun fichier sélectionné');
      return;
    }
    
    console.log('CSV file selected:', file.name, file.type, file.size);
    toast.info(`Fichier sélectionné: ${file.name}`);
    
    if (!importPropertyId) {
      toast.error('Veuillez d\'abord sélectionner une propriété');
      return;
    }

    setImporting(true);
    try {
      console.log('Importing CSV...');
      const response = await importApi.importCSV(file, importPropertyId);
      console.log('Import response:', response.data);
      const { imported, duplicates, errors } = response.data;
      
      if (imported > 0) {
        toast.success(`${imported} réservation(s) importée(s)`);
      }
      if (duplicates > 0) {
        toast.info(`${duplicates} doublon(s) ignoré(s)`);
      }
      if (errors && errors.length > 0) {
        toast.warning(`${errors.length} erreur(s) lors de l'import`);
        console.log('Import errors:', errors);
      }
      if (imported === 0 && duplicates === 0) {
        toast.warning('Aucune réservation importée. Vérifiez le format du fichier.');
      }
      
      setImportDialogOpen(false);
      loadReservations();
    } catch (error) {
      console.error('Import error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur inconnue';
      toast.error(`Erreur: ${errorMsg}`);
    } finally {
      setImporting(false);
    }
  }, [importPropertyId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropCSV,
    accept: { 
      'text/csv': ['.csv'], 
      'application/vnd.ms-excel': ['.csv', '.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.csv', '.txt'],
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    disabled: importing || properties.length === 0,
    maxSize: 20 * 1024 * 1024, // 20MB max
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.property_id) {
      toast.error('Veuillez sélectionner une propriété');
      return;
    }
    try {
      await reservationsApi.create({
        ...formData,
        total_price: parseFloat(formData.total_price) || 0,
        platform_commission: parseFloat(formData.platform_commission) || 0,
        cleaning_fee: parseFloat(formData.cleaning_fee) || 0,
        local_taxes: parseFloat(formData.local_taxes) || 0,
      });
      toast.success('Réservation créée');
      setDialogOpen(false);
      resetForm();
      loadReservations();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette réservation ?')) return;
    try {
      await reservationsApi.delete(id);
      toast.success('Réservation supprimée');
      loadReservations();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      property_id: selectedProperty !== 'all' ? selectedProperty : '',
      platform: 'airbnb',
      guest_name: '',
      guest_email: '',
      check_in: '',
      check_out: '',
      total_price: '',
      platform_commission: '',
      cleaning_fee: '',
      local_taxes: '',
      notes: '',
    });
  };

  const getPlatformBadge = (platform) => {
    const p = PLATFORMS.find((pl) => pl.value === platform);
    return p ? <Badge className={p.color}>{p.label}</Badge> : <Badge>{platform}</Badge>;
  };

  const totalRevenue = reservations.reduce((sum, r) => sum + (r.net_revenue || 0), 0);
  const totalNights = reservations.reduce((sum, r) => sum + (r.nights || 0), 0);

  return (
    <div className="space-y-4 md:space-y-6 animate-fade-in" data-testid="reservations-page">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold tracking-tight">Réservations</h1>
            <p className="text-muted-foreground text-sm hidden sm:block">Gérez vos réservations</p>
          </div>
        </div>
        
        {/* Action Buttons - Mobile optimized */}
        <div className="flex gap-2">
          {/* CSV Import Button */}
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="secondary" className="flex-1 sm:flex-none touch-manipulation h-10" data-testid="import-csv-button">
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Importer</span> CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md mx-4 bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-heading text-lg">Importer CSV</DialogTitle>
              </DialogHeader>
              {properties.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-muted-foreground mb-4">Vous devez d'abord créer une propriété avant d'importer des réservations.</p>
                  <Button 
                    type="button" 
                    variant="secondary" 
                    onClick={() => { setImportDialogOpen(false); window.location.href = '/proprietes'; }}
                  >
                    Créer une propriété
                  </Button>
                </div>
              ) : (
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Propriété *</Label>
                  <Select value={importPropertyId} onValueChange={setImportPropertyId}>
                    <SelectTrigger className="w-full" data-testid="import-property-select">
                      <SelectValue placeholder="Sélectionner une propriété" />
                    </SelectTrigger>
                    <SelectContent position="popper" className="z-[100]">
                      {properties.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors touch-manipulation relative z-[60] ${
                    isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  } ${importing || properties.length === 0 ? 'pointer-events-none opacity-50' : ''}`}
                  data-testid="csv-dropzone"
                >
                  <input {...getInputProps()} />
                  {importing ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <p className="text-sm text-muted-foreground">Import en cours...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Upload className="h-8 w-8" />
                      <p className="text-sm font-medium">Glissez un fichier</p>
                      <p className="text-xs">CSV, Excel (.xlsx) ou PDF</p>
                    </div>
                  )}
                </div>

                <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-medium">Formats acceptés :</p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• <strong>CSV / Excel / PDF</strong> avec tableau</li>
                    <li>• Colonnes : Date arrivée, Date départ, Prix, Nom</li>
                    <li>• Export Airbnb ou Booking.com</li>
                    <li>• Les doublons sont automatiquement ignorés</li>
                  </ul>
                </div>
              </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Add Reservation Button */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="flex-1 sm:flex-none bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow touch-manipulation h-10" data-testid="add-reservation-button">
                <Plus className="h-4 w-4 mr-2" />
                Ajouter
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg mx-4 bg-card border-border max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-lg">Nouvelle réservation</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-sm">Propriété *</Label>
                    <Select value={formData.property_id} onValueChange={(v) => setFormData({ ...formData, property_id: v })}>
                      <SelectTrigger data-testid="reservation-property-select" className="w-full h-10">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent position="popper" className="max-h-[200px] overflow-y-auto z-[100]">
                        {properties.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Plateforme</Label>
                    <Select value={formData.platform} onValueChange={(v) => setFormData({ ...formData, platform: v })}>
                      <SelectTrigger data-testid="reservation-platform-select" className="w-full h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent position="popper" className="z-[100]">
                        {PLATFORMS.map((p) => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Nom du voyageur *</Label>
                  <Input value={formData.guest_name} onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })} required className="h-10" data-testid="reservation-guest-name" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-sm">Arrivée *</Label>
                    <Input type="date" value={formData.check_in} onChange={(e) => setFormData({ ...formData, check_in: e.target.value })} required className="h-10" data-testid="reservation-checkin" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Départ *</Label>
                    <Input type="date" value={formData.check_out} onChange={(e) => setFormData({ ...formData, check_out: e.target.value })} required className="h-10" data-testid="reservation-checkout" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-sm">Prix total (€) *</Label>
                    <Input type="number" step="0.01" value={formData.total_price} onChange={(e) => setFormData({ ...formData, total_price: e.target.value })} required className="h-10" data-testid="reservation-price" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Commission (€)</Label>
                    <Input type="number" step="0.01" value={formData.platform_commission} onChange={(e) => setFormData({ ...formData, platform_commission: e.target.value })} className="h-10" data-testid="reservation-commission" />
                  </div>
                </div>

                <Button type="submit" className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 touch-manipulation" data-testid="reservation-submit">
                  Créer la réservation
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards - Mobile optimized */}
      <div className="grid grid-cols-3 gap-2 md:gap-4">
        <Card className="bg-card border-border/40">
          <CardContent className="pt-3 pb-2 px-3 text-center">
            <p className="text-[10px] md:text-xs text-muted-foreground uppercase">Réservations</p>
            <p className="text-lg md:text-2xl font-semibold tabular-nums">{reservations.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/40">
          <CardContent className="pt-3 pb-2 px-3 text-center">
            <p className="text-[10px] md:text-xs text-muted-foreground uppercase">Nuitées</p>
            <p className="text-lg md:text-2xl font-semibold tabular-nums">{totalNights}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/40">
          <CardContent className="pt-3 pb-2 px-3 text-center">
            <p className="text-[10px] md:text-xs text-muted-foreground uppercase">Revenu</p>
            <p className="text-lg md:text-2xl font-semibold tabular-nums text-primary">€{totalRevenue.toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Reservations List - Mobile Card View */}
      <Card className="bg-card border-border/40">
        <CardContent className="p-2 md:p-4">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground text-sm">Chargement...</div>
          ) : reservations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm mb-2">Aucune réservation</p>
              <p className="text-xs">Ajoutez une réservation ou importez un CSV</p>
            </div>
          ) : (
            <div className="space-y-2">
              {reservations.map((res) => (
                <div
                  key={res.id}
                  className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg"
                  data-testid={`reservation-row-${res.id}`}
                >
                  <div className={`w-1.5 h-14 rounded-full ${
                    res.platform === 'airbnb' ? 'bg-rose-500' : 
                    res.platform === 'booking' ? 'bg-blue-500' : 'bg-primary'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="font-medium text-sm truncate">{res.guest_name}</p>
                      {getPlatformBadge(res.platform)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {format(parseISO(res.check_in), 'd MMM', { locale: fr })} → {format(parseISO(res.check_out), 'd MMM', { locale: fr })} · {res.nights}n
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-semibold text-sm text-primary tabular-nums">€{(res.net_revenue || 0).toLocaleString()}</p>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(res.id)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 w-7 mt-1"
                      data-testid={`delete-reservation-${res.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
